import { Link } from "react-router-dom";
import { ShieldCheck, HeartHandshake, PhoneCall, Bell, MessageSquareText, ArrowRight } from "lucide-react";

const INK = "#0E1417";

export default function Impact() {
  return (
    <div className="min-h-screen bg-[#F4F7F6]">
      {/* Hero */}
      <section className="relative overflow-hidden grain-dark" style={{ backgroundColor: INK }}>
        <div
          className="pointer-events-none absolute -top-40 -right-32 w-[620px] h-[620px] rounded-full opacity-[0.22] blur-[120px]"
          style={{ background: "radial-gradient(circle, #2E9D96 0%, transparent 70%)" }}
        />
        <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-16 md:pt-28 md:pb-20">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-10">
            <div className="w-9 h-9 rounded-xl bg-[#2E9D96] flex items-center justify-center text-white">
              <MessageSquareText size={17} />
            </div>
            <span className="font-grotesk font-bold text-[22px] tracking-tight text-white">m8 im here</span>
          </Link>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#3FC1B7] font-semibold mb-5">A public-good safety net</p>
          <h1 className="font-grotesk font-bold text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] text-white max-w-3xl">
            Nobody should slip through the cracks in silence.
          </h1>
          <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">
            m8 im here is an always-on companion that checks in on people every day — and raises the alarm
            to someone they trust if they go quiet or reach a crisis point. The safety features are
            <span className="text-white font-semibold"> free for everyone, forever.</span> This page is for
            partners, clinicians, and funders who want to help us reach more people.
          </p>
        </div>
      </section>

      {/* The problem */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { big: "3,326", small: "Australians lost to suicide in 2024 — around nine people every single day." },
            { big: "46", small: "The median age. Not the young alone — people in the middle of life, often silently carrying it while everyone assumes they're fine." },
            { big: "76%", small: "were men — the ones least likely to ever say \u201cI'm not okay\u201d out loud. m8 im here asks first, every day." },
          ].map((c, i) => (
            <div key={i} className="rounded-3xl bg-white border border-slate-200/70 p-7 shadow-sm">
              <p className="font-grotesk font-bold text-4xl text-[#1F7B75]">{c.big}</p>
              <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">{c.small}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate-400">Source: Australian Bureau of Statistics, <em>Causes of Death, Australia 2024</em> (preliminary revised) — 3,326 deaths by suicide, crude rate 12.2 per 100,000, median age 46.0 years, 76.5% male.</p>
      </section>

      {/* The safety net */}
      <section className="bg-white border-y border-slate-200/70">
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-20">
          <p className="text-xs uppercase tracking-[0.26em] text-[#2E9D96] font-semibold mb-4">What we've built</p>
          <h2 className="font-grotesk font-semibold text-3xl md:text-4xl text-slate-900 max-w-2xl">
            A welfare-check safety net, built into a daily companion.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {[
              { icon: <MessageSquareText size={20} />, t: "A daily check-in", d: "Every day, m8 im here gently asks how someone is doing — and remembers, so support builds over time." },
              { icon: <Bell size={20} />, t: "Notices when someone goes quiet", d: "If a person stops responding, we nudge them at 24 hours and alert their trusted contact at 48 — by email and SMS." },
              { icon: <PhoneCall size={20} />, t: "A crisis is never met with a paywall", d: "If someone expresses they're in danger, help and real local hotlines appear instantly — free, even for lapsed accounts." },
              { icon: <HeartHandshake size={20} />, t: "One tap to pull a loved one in", d: "In a dark moment, a person can alert their trusted person with a single tap — no chat content is ever shared." },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-4 rounded-3xl border border-slate-200/70 p-6">
                <div className="w-11 h-11 rounded-2xl bg-[#0E1417] text-[#3FC1B7] flex items-center justify-center shrink-0">{f.icon}</div>
                <div>
                  <h3 className="font-grotesk font-semibold text-lg text-slate-900">{f.t}</h3>
                  <p className="text-slate-600 leading-relaxed text-[15px] mt-1">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder's story */}
      <section className="bg-[#0E1417] grain-dark">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#3FC1B7] font-semibold mb-6">Why I built this</p>
          <div className="space-y-5 text-slate-300 text-[17px] leading-relaxed">
            <p>
              A few years ago I was diagnosed with Parkinson's. Every day I'd be home by three for what I
              came to call my afternoon "shakedown" — my body going into chaos as the medication wore off,
              legs moving on their own, soaked in sweat and utterly exhausted by six. Over seven months it got
              severely worse, and by July I'd had enough. I was one of the lucky ones: a new medication came
              along that, honestly, saved my life.
            </p>
            <p>
              But I never forgot how that time felt. I learned how quickly loneliness closes in when you're
              unwell — how you stop reaching out, not because you don't want people, but because you have
              nothing left to give. On the outside I was still going to work and getting home. On the inside,
              some days, I felt completely alone and very low.
            </p>
            <p>
              m8 im here is the friend I needed back then. Someone who checks in every single day, remembers what
              you're going through, notices when you go quiet, and quietly pulls a loved one in if you drop off
              the radar. It won't cure anyone. But if it means one person feels that they matter — and that
              someone would know if they weren't okay — then it's the most worthwhile thing I've ever done.
            </p>
            <p className="text-white font-semibold">
              If it saves even one life, every hard afternoon was worth it.
            </p>
          </div>
          <p className="mt-8 font-grotesk text-lg text-white">— Craig</p>
        </div>
      </section>

      {/* Why fund it */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-20">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.26em] text-[#2E9D96] font-semibold mb-4">Why partner with us</p>
            <h2 className="font-grotesk font-semibold text-3xl text-slate-900 leading-snug">
              The safety net costs cents — reach is the only thing standing between it and more lives.
            </h2>
            <ul className="mt-6 space-y-3 text-slate-600 text-[15px]">
              <li className="flex items-start gap-3"><ShieldCheck className="text-[#2E9D96] shrink-0 mt-0.5" size={18} /> Safety features are free for every user, regardless of ability to pay.</li>
              <li className="flex items-start gap-3"><ShieldCheck className="text-[#2E9D96] shrink-0 mt-0.5" size={18} /> Delivered by email today, SMS-enabled, privacy-first — contacts only ever get "please check in," never someone's words.</li>
              <li className="flex items-start gap-3"><ShieldCheck className="text-[#2E9D96] shrink-0 mt-0.5" size={18} /> Funding goes to reach: getting the net in front of the isolated, the unwell, and those hit hardest by hard times.</li>
            </ul>
          </div>
          <div className="rounded-3xl bg-[#0E1417] p-8 text-white grain-dark">
            <p className="text-[11px] uppercase tracking-[0.26em] text-[#3FC1B7] font-semibold mb-4">Partner with m8 im here</p>
            <p className="text-slate-300 leading-relaxed">
              We're seeking mental-health and community partners to extend this safety net to more people across
              Australia. If your organisation funds suicide-prevention or welfare-check initiatives, we'd love to talk.
            </p>
            <a
              href="mailto:questions@m8imhere.com?subject=m8%20im%20here%20partnership%20%2F%20funding"
              data-testid="impact-contact-btn"
              className="group inline-flex items-center gap-2 mt-7 bg-[#2E9D96] hover:bg-[#38B3AB] text-white font-semibold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
            >
              Start a conversation
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />
            </a>
            <p className="mt-4 text-sm text-slate-400">questions@m8imhere.com</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200/70">
        <div className="max-w-5xl mx-auto px-6 py-8 text-sm text-slate-500 flex flex-wrap items-center justify-between gap-4">
          <Link to="/" className="font-grotesk font-semibold text-slate-700" data-testid="impact-home-link">m8 im here</Link>
          <p>If you or someone you know is in crisis, call 000, or Lifeline on 13 11 14.</p>
        </div>
      </footer>
    </div>
  );
}
